<script setup>
import { ref,inject } from 'vue';
import emitter from '@/utils/bus';
import { useConfigStore } from '@/store/config';
import { toastFail,toastSuccess } from '@/utils/common.js';
import { submitFeedback } from '@/api/setting';
import { useI18n } from 'vue-i18n';
const { t } = useI18n();
const $swal = inject("$swal");
const config = useConfigStore();
const isShow = ref(false);
const email = ref("");
const content = ref("");
const isLoading = ref(false);

function validateEmail(email) {
  const re = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
  return re.test(email);
}

function getTodayDate() {
  const d = new Date();
  return `${d.getFullYear()}-${d.getMonth()+1}-${d.getDate()}`;
}

const handleSubmit = async () => {
  if (!validateEmail(email.value)) {
    toastFail($swal, t("message.Pleaseenteravalidemailaddress"));
    return;
  }
  const last = localStorage.getItem('feedback_submitted');
  const today = getTodayDate();
  if (last === today) {
    toastFail($swal, t("message.Youhavealreadyfeedbackedtoday,pleasecomebacktomorrow"));
    return;
  }
  try {
    isLoading.value = true;
    const res = await submitFeedback({
      content: content.value,
      email: email.value
    });
    if(res.code == 200){
      localStorage.setItem('feedback_submitted', today);
      toastSuccess($swal, t("message.Feedbacksubmittedsuccessfully,thankyouforyourvaluablefeedback"));
      isShow.value = false;
      email.value = '';
      content.value = '';
    }else{
      toastFail($swal, t("message.Feedbacksubmissionfailed,pleaseretriy"));
    }
  } finally {
    isLoading.value = false;
  }
}

const closeFeedback = () => {
    isShow.value = false;
};
emitter.on("showFeedback", (res) => {
    isShow.value = res;
});
</script>

<template>
    <div class="modal-overlay" v-if="isShow">
        <div class="feedback-container">
            <div class="top-bar">
                <div class="title">{{ $t("message.Feedback") }}</div>
                <div class="close" @click="closeFeedback">×</div>
            </div>
            <div class="content">
                <input type="email" :placeholder="$t('message.Pleaseenteryouremailaddress')" v-model="email">
                <textarea :placeholder="$t('message.Inputfeedbackcontent')" v-model="content"></textarea>
            </div>
            <div class="button">
                <button 
                    @click="handleSubmit" 
                    :disabled="isLoading"
                    :class="{ 'loading': isLoading }"
                >
                    <span v-if="!isLoading">{{ $t("message.Submitfeedback") }}</span>
                    <span v-else class="loading-spinner"></span>
                </button>
            </div>
        </div>
    </div>
</template>

<style scoped lang="scss">
@use "@/styles/surfaces" as surface;

.modal-overlay {
  @include surface.dialog-overlay;
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  background: rgba(30, 30, 30, 0.35);
  backdrop-filter: blur(12px);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
}

.feedback-container {
  @include surface.dialog-panel;
  background-color: #232323;
  box-shadow: 0 4px 24px rgba(0, 0, 0, 0.4);
  position: relative;
}

.top-bar {
  @include surface.dialog-header;
  margin-bottom: 8px;

  .close {
    @include surface.close-button;
    color: #ffffff;
    font-weight: bold;
  }
}

.title {
  @include surface.title;
  font-weight: bold;
  color: #fff;
  text-align: left;
}

.content {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.content input,
.content textarea {
  width: 100%;
  padding: 7px 10px;
  min-height: 40px;
  background: #2d2d2d;
  border: 1px solid #616161;
  border-radius: 10px;
  font-size: 16px;
  font-family: inherit;
  line-height: 1.5;
  color: #fff;
  outline: none;
  box-sizing: border-box;
  box-shadow: 0 0 0 2px rgba(136, 136, 248, 0.15);
}

.content input::placeholder,
.content textarea::placeholder {
  color: #aaa;
}

.content textarea {
  min-height: 80px;
  resize: vertical;
}

.button {
  margin-top: 8px;
  text-align: center;
}

.button button {
  @include surface.button;
  width: 100%;
  background-color: v-bind("config.themes_color");
  color: #fff;
  border: none;
  cursor: pointer;
  font-weight: bold;
  position: relative;
}

.button button:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}

.loading-spinner {
  display: inline-block;
  width: 20px;
  height: 20px;
  border: 2px solid #ffffff;
  border-radius: 50%;
  border-top-color: transparent;
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

</style>